import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useActionGuard } from '../../../../hooks/useActionGuard.ts';
import { useNavigate } from 'react-router-dom';
import { getCommerce, getCommerceMaterials } from '../../../../services/commerceService.ts';
import {
  extractCommerceMaterialsDataArray,
  flattenCategories,
  parseCommerceMaterialsPayload,
} from '../MisMaterialesComercio/commerceMaterialsModel.ts';
import { buildNavigationParams, getUserDisplayName } from '../../../../utils/navigationHelpers.ts';
import { listUserCommerceEntriesFromUserData } from '../../../../utils/userCommerces.ts';
import styles from './MisComercioGestion.module.scss';

interface MisComercioGestionProps {
  userData: Record<string, unknown> | null | undefined;
  setInfoReady: (ready: boolean) => void;
}

function pickString(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function logoSrcFromRaw(raw: Record<string, unknown>): string | null {
  const logo = raw.logo ?? raw.logoUrl ?? raw.image_profile_commerce ?? raw.commerceLogo;
  if (typeof logo !== 'string') return null;
  const s = logo.trim();
  return s.length > 0 ? s : null;
}

type CommerceDeliveryScope = 'LOCAL' | 'LISTED_ZONES' | 'NATIONWIDE';

function deliveryScopeFromRaw(raw: Record<string, unknown>): CommerceDeliveryScope | null {
  const asScope = (v: unknown): CommerceDeliveryScope | null =>
    v === 'LOCAL' || v === 'LISTED_ZONES' || v === 'NATIONWIDE' ? v : null;

  const g = raw.deliveryGeo ?? raw.delivery_geo;
  if (g != null && typeof g === 'object') {
    const go = g as Record<string, unknown>;
    const s = asScope(go.deliveryScope ?? go.delivery_scope);
    if (s) return s;
  }
  const s = asScope(raw.deliveryScope ?? raw.delivery_scope);
  if (s) return s;
  return null;
}

function deliveryRadiusKmFromRaw(raw: Record<string, unknown>): number | null {
  const fromRoot = raw.deliveryRadiusKm ?? raw.delivery_radius_km;
  if (typeof fromRoot === 'number' && Number.isFinite(fromRoot)) return fromRoot;
  if (typeof fromRoot === 'string' && fromRoot.trim() && Number.isFinite(Number(fromRoot))) return Number(fromRoot);

  const g = raw.deliveryGeo ?? raw.delivery_geo;
  if (g != null && typeof g === 'object') {
    const geo = g as Record<string, unknown>;
    const fromGeo = geo.deliveryRadiusKm ?? geo.delivery_radius_km;
    if (typeof fromGeo === 'number' && Number.isFinite(fromGeo)) return fromGeo;
    if (typeof fromGeo === 'string' && fromGeo.trim() && Number.isFinite(Number(fromGeo))) return Number(fromGeo);
  }

  return null;
}

function formatKm(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

function coverageTypeFromRaw(raw: Record<string, unknown>): string {
  const scope = deliveryScopeFromRaw(raw);
  if (scope === 'NATIONWIDE') return 'Cobertura en todo el país';
  if (scope === 'LISTED_ZONES') return 'Cobertura por zonas seleccionadas';
  if (scope === 'LOCAL') {
    const radiusKm = deliveryRadiusKmFromRaw(raw);
    if (radiusKm != null) return `${formatKm(radiusKm)} km alrededor del punto`;
    return 'Radio alrededor del punto seleccionado';
  }
  return '—';
}

function regionSummaryFromRaw(raw: Record<string, unknown>): { label: string; line: string } {
  const scope = deliveryScopeFromRaw(raw);
  const zone = pickString(raw, 'zone', 'zona');
  const jurisNested =
    raw.jurisdiccion != null && typeof raw.jurisdiccion === 'object'
      ? pickString(raw.jurisdiccion as Record<string, unknown>, 'name', 'nombre')
      : '';
  const jurisFlat = pickString(raw, 'jurisdiccionNombre', 'jurisdictionName');
  const juris = (jurisNested || jurisFlat).trim();

  if (scope === 'NATIONWIDE') {
    const line = zone || 'Todo el país';
    return { label: 'Ámbito territorial', line };
  }
  if (scope === 'LISTED_ZONES') {
    const line = [juris, zone].filter(Boolean).join(' · ');
    return { label: 'Provincias y zonas del listado', line: line || '—' };
  }
  if (scope === 'LOCAL') {
    const snapshot = pickString(raw, 'geocode_query_snapshot', 'geocodeQuerySnapshot');
    const line = zone || snapshot || juris || '—';
    return { label: 'Punto de referencia del mapa', line };
  }
  const line = [juris, zone].filter(Boolean).join(' · ');
  return { label: 'Provincia / zona', line: line || '—' };
}

function shippingStatusFromRaw(raw: Record<string, unknown>): string {
  const d = raw.delivery;
  const enabled = d === true || d === 1 || (typeof d === 'string' && (d === 'true' || d === '1'));
  return enabled ? 'Hace envíos' : 'No hace envíos';
}

function withHttp(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url.replace(/^\/\//, '')}`;
}

function normalizeUpdatedAt(raw: Record<string, unknown>): string | null {
  const value = pickString(raw, 'updated_at', 'updatedAt');
  if (!value) return null;
  const time = Date.parse(value);
  if (Number.isNaN(time)) return value;
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(time));
}

function extractCommercePayload(res: { data?: unknown }): Record<string, unknown> | null {
  const root = res.data;
  if (root == null || typeof root !== 'object' || Array.isArray(root)) return null;
  const r = root as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return r;
}

function CommercePreview({
  raw,
  commerceName,
  materialsCount,
  updatedAt,
}: {
  raw: Record<string, unknown>;
  commerceName: string;
  materialsCount: number | null;
  updatedAt: string | null;
}) {
  const desc = pickString(raw, 'description', 'descripcion');
  const region = regionSummaryFromRaw(raw);
  const web = pickString(raw, 'url_site_web', 'urlSiteWeb');
  const deliveryLine = shippingStatusFromRaw(raw);
  const coverageTypeLine = coverageTypeFromRaw(raw);
  const logoSrc = logoSrcFromRaw(raw);
  const initial = commerceName.trim().charAt(0).toUpperCase() || 'C';

  return (
    <div className={styles.previewLayout}>
      <div className={styles.previewHeader}>
        <div className={styles.previewAvatar} aria-hidden>
          {logoSrc ? <img className={styles.previewAvatarImg} src={logoSrc} alt="" /> : initial}
        </div>
        <div className={styles.previewIdentity}>
          <h3 className={styles.commerceName}>{commerceName}</h3>
          <div className={styles.previewWebRow}>
            <span className={styles.previewLabel}>Sitio web</span>
            <div className={web ? styles.previewBodyInline : styles.previewBodyEmptyInline}>
              {web ? (
                <a className={styles.previewLink} href={withHttp(web)} target="_blank" rel="noopener noreferrer">
                  {web}
                </a>
              ) : (
                '—'
              )}
            </div>
          </div>
          <p className={styles.metaRow}>
            <span className={styles.metaItem}>
              Materiales cargados: {typeof materialsCount === 'number' ? materialsCount : '—'}
            </span>
            <span className={styles.metaItem}>Última actualización: {updatedAt || '—'}</span>
          </p>
        </div>
      </div>
      <div className={styles.previewGrid}>
        <div className={styles.previewBlock}>
          <div className={styles.previewLabel}>Descripción</div>
          <div className={desc ? styles.previewBody : styles.previewBodyEmpty}>{desc || '—'}</div>
        </div>
        <div className={styles.previewBlock}>
          <div className={styles.previewLabel}>{region.label}</div>
          <div className={region.line && region.line !== '—' ? styles.previewBody : styles.previewBodyEmpty}>
            {region.line}
          </div>
        </div>
        <div className={styles.previewBlock}>
          <div className={styles.previewLabel}>Tipo de cobertura</div>
          <div className={coverageTypeLine ? styles.previewBody : styles.previewBodyEmpty}>
            {coverageTypeLine || '—'}
          </div>
        </div>
        <div className={styles.previewBlock}>
          <div className={styles.previewLabel}>Entrega</div>
          <div className={deliveryLine ? styles.previewBody : styles.previewBodyEmpty}>{deliveryLine || '—'}</div>
        </div>
      </div>
    </div>
  );
}

interface CommerceStats {
  materialsCount: number | null;
  updatedAt: string | null;
}

const MisComercioGestion: React.FC<MisComercioGestionProps> = ({ userData, setInfoReady }) => {
  const navigate = useNavigate();
  const { guard } = useActionGuard();
  useEffect(() => {
    setInfoReady(true);
  }, [setInfoReady]);

  const commerceEntries = useMemo(() => listUserCommerceEntriesFromUserData(userData), [userData]);
  const [detailRawById, setDetailRawById] = useState<Record<number, Record<string, unknown>>>({});
  const [statsById, setStatsById] = useState<Record<number, CommerceStats>>({});

  useEffect(() => {
    if (!commerceEntries.length) {
      setDetailRawById({});
      setStatsById({});
      return;
    }
    const seed: Record<number, Record<string, unknown>> = {};
    const seedStats: Record<number, CommerceStats> = {};
    for (const e of commerceEntries) seed[e.id] = e.raw;
    for (const e of commerceEntries) {
      seedStats[e.id] = {
        materialsCount: null,
        updatedAt: normalizeUpdatedAt(e.raw),
      };
    }
    setDetailRawById(seed);
    setStatsById(seedStats);
    let cancelled = false;
    void (async () => {
      const merged: Record<number, Record<string, unknown>> = { ...seed };
      const mergedStats: Record<number, CommerceStats> = { ...seedStats };
      await Promise.all(
        commerceEntries.map(async (e) => {
          let detailRaw: Record<string, unknown> | null = null;
          try {
            const res = await getCommerce(e.id);
            const d = extractCommercePayload(res);
            if (d && !cancelled) {
              detailRaw = d;
              merged[e.id] = d;
            }
          } catch {
            merged[e.id] = e.raw;
          }
          try {
            const materialsRes = await getCommerceMaterials(e.id);
            const dataArray = extractCommerceMaterialsDataArray(materialsRes?.data);
            if (dataArray != null) {
              const categories = parseCommerceMaterialsPayload(dataArray, e.id);
              mergedStats[e.id] = {
                materialsCount: flattenCategories(categories).length,
                updatedAt: normalizeUpdatedAt(detailRaw ?? merged[e.id] ?? e.raw),
              };
            } else {
              mergedStats[e.id] = {
                materialsCount: 0,
                updatedAt: normalizeUpdatedAt(detailRaw ?? merged[e.id] ?? e.raw),
              };
            }
          } catch {
            mergedStats[e.id] = {
              materialsCount: null,
              updatedAt: normalizeUpdatedAt(detailRaw ?? merged[e.id] ?? e.raw),
            };
          }
        })
      );
      if (!cancelled) {
        setDetailRawById(merged);
        setStatsById(mergedStats);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commerceEntries]);
  const userId = userData?.id != null ? String(userData.id) : '';
  const displayName = getUserDisplayName(userData);
  const profileNavToGestionComercio = useMemo(
    () => buildNavigationParams('Gestión', userId, displayName ?? 'Perfil', 'comercio'),
    [displayName, userId]
  );
  const goToCommerceAlta = useCallback(() => {
    guard('register_commerce', () => navigate('/comercios/alta'))();
  }, [guard, navigate]);
  const accountType =
    userData && typeof userData === 'object' ? (userData as { type?: string }).type : undefined;

  if (!userId) {
    return null;
  }

  if (!commerceEntries.length) {
    return (
      <div className={styles.wrap}>
        <h2 className={styles.title}>Mi comercio</h2>
        <p className={styles.intro}>No tenés un comercio registrado.</p>
        <button type="button" className={styles.primaryLink} onClick={goToCommerceAlta}>
          Dar de alta
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <h2 className={styles.title}>Mi comercio</h2>
      <p className={styles.intro}>Vista de lo que figura hoy en tu cuenta; para editar usá los accesos de abajo.</p>
      <ul className={styles.list}>
        {commerceEntries.map((c) => (
          <li key={c.id} className={styles.card}>
            <CommercePreview
              raw={detailRawById[c.id] ?? c.raw}
              commerceName={c.name}
              materialsCount={statsById[c.id]?.materialsCount ?? null}
              updatedAt={statsById[c.id]?.updatedAt ?? null}
            />
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.actionLink}
                onClick={() => navigate(`/comercios/edit/${c.id}${profileNavToGestionComercio}`)}
              >
                Editar datos del comercio
              </button>
              {accountType !== 'JOBBOARD' ? (
                <button
                  type="button"
                  className={styles.actionLink}
                  onClick={() =>
                    navigate(`/comercios/lista-precios?commerceId=${c.id}&${profileNavToGestionComercio.slice(1)}`)
                  }
                >
                  Cargar/actualizar planilla de precios
                </button>
              ) : null}
              {accountType !== 'JOBBOARD' ? (
                <button
                  type="button"
                  className={styles.actionLink}
                  onClick={() =>
                    navigate(`/comercios/catalogo?commerceId=${c.id}&${profileNavToGestionComercio.slice(1)}`)
                  }
                >
                  Ver catálogo
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MisComercioGestion;
